<template lang="pug">
include Card.pug
</template>

<script>
/* eslint-disable vue/no-unused-components */
import EntityMixin from '@/components/Base/EntityMixin';
import HyperlinkMixin from '@/components/Base/HyperlinkMixin';

export default {

    props: {
        imageComponent: {    type: String, default: 'image-box' },
        className: {         type: String },
        format: {            type: String },
        header: {            type: Boolean, default: true },
        imageAsBackground: { type: Boolean },
        zoom: {              type: Number }, // image zoom, set to query params
        viewerZoom: {        type: Number }, // zoom value for dynamic viewerjs
        item: {              type: Object, required: true },
        linkTo: {            type: [Object, Boolean], default: true },
        targetTab: {         type: String },
        media: {             type: Boolean, default: true },
        resolution: {        type: Object },
        social: {            type: Boolean, default: true },
        socialImage: {       type: Boolean, default: false }, // heart icon on hover
        contents: {          type: Boolean, default: true },
        showCredits: {       type: Boolean, default: false },
        showCopyright: {     type: Boolean, default: false },
        showArticleLinks: {  type: Boolean, default: false },
        showNotAvailable: {  type: Boolean },
        useMasonrySizing: {  type: Boolean },
        usePalette: {        type: Boolean, default: false },
        useCoverImage: {     type: Boolean }, // default is ingress image
        clickOverride: {     type: Function } // override default click action
    },

    mixins: [ EntityMixin, HyperlinkMixin ],

    components: {
        ArticleLinks: () => import('@/components/ArticleLinks')
    },

    computed: {
        title: function () {
            return this.getTitle();
        },

        /**
         * <img alt="...text..."
         */
        imageAltText: function () {
            return '';
        },

        computedClassName: function () {
            return this.getClassName();
        },

        computedStyle: function () {
            return this.getStyle();
        },

        computedImageSrc: function () {
            return this.getCardImageSrc(this.item);
        },

        computedRoute: function () {
            return this.getMyRoute(this.item);
        },

        /**
         * Image resolution, eg. {x: 1800}
         */
        imageResolution: function () {
            return this.resolution;
        },

        /**
         * Image orientation based on actual `ImageInfo.Width / Height` or given prop `resolution`.
         */
        imageOrientation: function () {
            return this.$helpers.getImageOrientation(this.item, this.resolution);
        },

        /**
         * If image has FocusPoint, it is (crudly) reflected to card's css ClassName,
         * ie. card-focus-top (-bottom), card-focus-left (-right)
         */
        imageFocusClass: function () {
            let className = '';
            const imageInfo = this.$helpers.getImageInfo(this.item, true); // FIXME: ImageInfo, CoverImageInfo, IngressImageInfo?
            if (imageInfo && imageInfo.FocusPoint) {
                const fp = imageInfo.FocusPoint;
                if (fp[0] < 0) {
                    className += ' card-focus-left';
                }
                else if (fp[0] > 0) {
                    className += ' card-focus-right';
                }
                if (fp[1] < 0) {
                    className += ' card-focus-top';
                }
                else if (fp[1] > 0) {
                    className += ' card-focus-bottom';
                }
            }
            return className;
        },

        /**
         * Used in image not found template
         */
        defaultImageSrc: function () {
            return null;
        },

        coverImageSrc: function () {
            const imageAttrs = {};
            if (this.resolution) {
                imageAttrs.resolution = this.resolution;
            }
            return this.$helpers.getCoverImageSrc(this.item, imageAttrs);
        },

        ingressImageSrc: function () {
            const imageAttrs = {};
            if (this.resolution) {
                imageAttrs.resolution = this.resolution;
            }
            return this.$helpers.getIngressImageSrc(this.item, imageAttrs);
        },

        caption: function () {
            return this.item && this.item.Caption;
        },

        creditLine: function () {
            return this.item && this.item.CreditLine;
        },

        copyright: function () {
            return this.item && this.item.Copyright;
        }
    },

    methods: {
        getClassName: function () {
            let className = this.className;
            if (this.imageOrientation) {
                className += ` card-${this.imageOrientation}`;
            }
            if (this.imageFocusClass) {
                className += ` ${this.imageFocusClass}`;
            }
            return className;
        },

        /**
         * Entity card linking can be constructed in multiple ways.
         *
         * The basic way is to leave `this.linkTo` prop to default,
         * or set it as `true` and define getRoute() method
         * in the implementing Card component.
         *
         * An alternative link (defined in the parent element) is
         * possible by defining `linkTo` as a vue route object.
         *
         * If the link should open in a new tab, the `targetTab` property
         * should indicate this.
         *
         */
        getMyRoute: function (item) {
            if (typeof this.linkTo === 'object') {
                return this.linkTo;
            }
            else if (this.linkTo === false) {
                return '#';
            }
            else {
                return this.getCardRoute(item);
            }
        },

        /**
         * When the card router-link is clicked, this method can
         * still prevent the link from being activated, instead
         * of opening it up in a new window tab.
         *
         * The clicked item is also saved to global store to help
         * in forming the context when the link is opened. For instance,
         * the random artwork that was clicked should appear on the new page.
         */
        onClick: function (item, event) {
            event.preventDefault();
            event.stopPropagation();
            event.cancelBubble = true;

            if (this.clickOverride) {
                return this.clickOverride(item, event);
            }

            if (!this.linkTo) {
                // TODO: check for linkTo Boolean, Object truthy
                this.$emit('click', item, event);
                return;
            }

            // set the ingress image or the item itself
            if (item && this.resource === 'artworks') {
                this.$store.commit('ux/setContextArtwork', item);
            }

            // should the link be opened into a new tab, or to the current one?
            if (this.targetTab && process.client) {
                const url = this.$router.resolve(this.getMyRoute(item)).href;
                // console.log("open in new tab", routeData.href);
                window.open(url, this.targetTab, 'noreferrer');
            }
            else {
                this.$router.push(this.getMyRoute(item));
            }
            return false;
        },

        /**
         * Get image for the Card.
         * First try ingress/cover image, then default image, then base image.
         */
        getCardImageSrc: function (item, imageAttrs) {
            let src;
            if (this.useCoverImage) {
                src = this.coverImageSrc || this.defaultImageSrc || this.$helpers.getImageSrc(item, imageAttrs);
            }
            else {
                src = this.ingressImageSrc || this.defaultImageSrc || this.$helpers.getImageSrc(item, imageAttrs);
            }
            return src;
        },

        /**
         * Card width must be hard-limited for the header ellipsis overflow to work.
         */
        getStyle: function () {
            let style = '';
            const wh = this.$helpers.getMasonryWidthHeight(this.resolution, this.item);
            if (wh && wh.width) {
                style += `width: ${wh.width}px;`;
            }
            if (this.linkTo || this.clickOverride) {
                style += 'cursor: pointer;';
            }
            return style;
        },

        /**
         * Implement in card
         */
        getCardRoute: function (item) {}
    }
};
</script>

<style lang="scss">
// @import '~assets/styles/mixins';

.card {
    display: flex;
    flex-basis: auto !important;
    flex-direction: column;
    flex-grow: 1;
    flex-wrap: wrap;
    width: auto;
    height: auto;
    min-width: 280px;
    min-height: min-content;
    margin: 0;
    margin-top: auto;
    overflow: hidden;
    font-family: $helvetica-bold;
    color: black;
    text-align: left;
    background-color: transparent;
    box-shadow: none;
    h3, h4 {
        font-family: $helvetica-roman;
        font-size: 1.5rem;
        line-height: 1;
    }
    a, .card-link {
        display: inline-block;
        flex: 1;
        width: inherit;
        height: inherit; // NOTE: height: 100% breaks images on Firefox!
        color: inherit;
        &:hover {
            color: inherit;
            h1, h2, h3, h4 {
                color: inherit;
            }
        }
    }
    .tile {
        width: 100%;
    }
    .card-header {
        z-index: 4500; // below navbar dropdown
        display: flex;
        flex-direction: column;
        align-self: flex-start;
        width: inherit;
        color: inherit;
        .card-link {
            display: flex;
            flex-direction: column;
            flex-wrap: wrap;
            &:hover {
                color: inherit !important;
            }
        }
        .card-header-title {
            display: inline-flex;
            flex-direction: column;
            align-items: left;
            width: 100%;
            min-width: unset;
            margin: 0;
            padding: 0.125rem;
            line-height: normal;
            text-align: left;
            cursor: default;
            @include nowrap;
            h1, h2 {
                @include nowrap;
                margin: 0;
                margin-right: auto;
                font-size: 1em;
                letter-spacing: 0;
                white-space: nowrap;
            }
        }
    }
    .card-contents {
        z-index: 4500; // below navbar dropdown
        flex-grow: 1;
        height: auto;
    }
    .card-attribution {
        margin: 1rem 0 0;
        padding: 0.75rem 0.25rem 0.75rem 0.75rem;
        text-align: left;
        .credit-line {
            display: inline-flex;
            align-items: center;
            .icon-taide-art {
                width: 22px;
                height: 22px;
                margin: auto 12px auto 0;
            }
        }
        .credit-line,
        .copyright {
            font-family: $helvetica-medium;
            font-weight: normal;
            font-size: 1rem;
            line-height: 0.95;
            color: $dark-gray-3;
            span {
                color: inherit;
            }
        }
    }
    .social-button-wrapper {
        z-index: 4500; // below navbar dropdown
        display: inline-block;
        align-self: center;
        justify-self: right;
        margin-left: auto;
    }
    &.no-link {
        cursor: default;
    }
    div.ingress-text {
        display: block;
        flex-direction: column;
        font-family: $helvetica-roman;
        font-size: 1rem;
    }
    .card-image {
        display: flex;
        flex-grow: 1;
        align-items: stretch;
        align-self: stretch;
        width: auto;
        height: 100%;
    }
    .image-tool-icons {
        height: 0;
    }
    .card-hero {
        width: auto;
    }
    .collection {
        // hide collection by default
        display: none;
    }
    .duration {
        font-family: $helvetica-roman;
        font-size: 12px;
    }

    &:hover {
        .social-button-wrapper {
            opacity: 1;
        }
    }

    // slightly different layout when social block is in header
    &.card-social-header {
        .card-header-title {
            // max-width: 71%; // room for social button with nowrap
            font-family: 'Sabon LT W01 Bold', serif;
            font-weight: normal;
            @include nowrap;
            h2.title {
                font-size: 1.1rem;
                line-height: 1.25;
            }
            // all child elements
            & > * {
                display: block !important;
                @include nowrap;
            }
        }
        .card-header {
            width: 100%;
        }
        .card-header .card-link {
            flex-direction: row !important;
            // .card-header-title {
            //     max-width: 100%;
            // }
            .social-button-wrapper {
                align-self: auto;
                margin-top: 0.25rem;
            }
        }
        @media screen and (min-width: $breakpoint-desktop) {
            .card-header .card-link {
                flex-wrap: nowrap;
            }
        }
    }

    // hover box-shadow and image lighten effect
    &.collection-card,
    &.organization-card,
    &.event-card,
    &.artwork-card {
        margin: 0.375rem; // margin for box-shadow
        &:hover {
            box-shadow: 0 0 1.15rem $medium-gray;
            figure .image {
                background-color: rgba(100, 100, 100, 1);
                background-blend-mode: hard-light;
            }
        }
    }

    // first and last (even) elements
    // &:first-child, &:last-child:nth-child(even) {}
    @media screen and (max-width: $breakpoint-tablet - 1) {
        flex-direction: column;
    }
}
</style>
